import { HttpNetworkError } from "@inertiajs/core";
import * as Sentry from "@sentry/react";

// Production serves the apex `livetravily.com`, so the subdomain group has to be optional —
// a `.*\.` pattern matches only `www.` and silently drops sentry-trace/baggage everywhere else,
// leaving frontend errors unjoinable to their Rails transaction. The trailing anchor keeps
// lookalike hosts (`livetravily.com.example`) from receiving those headers.
const TRAVILY_ORIGIN = /^https:\/\/([a-z0-9-]+\.)*livetravily\.com(\/|$)/;

const sentryDsn = import.meta.env.VITE_SENTRY_DSN_FRONTEND;
const environment = import.meta.env.MODE;

/**
 * A prefetch whose transport dies has to be left to reject — cancelling Inertia's `networkError`
 * event would skip the only path that clears the request from its in-flight prefetch registry — so
 * the rejection lands on Sentry's `unhandledrejection` hook after `registerNetworkErrorHandling`
 * has already decided whether the failure deserved an enriched warning. Nothing on the event side
 * can stop that hook: Sentry instruments the `window.onunhandledrejection` property, which the
 * browser calls whatever a listener's `preventDefault()` does. So the duplicate is dropped here.
 */
function isAlreadyHandledNetworkError(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  return (
    hint.originalException instanceof HttpNetworkError && event.exception?.values?.[0]?.mechanism?.handled === false
  );
}

if (sentryDsn && environment === "production") {
  Sentry.init({
    dsn: sentryDsn,
    environment,
    release: import.meta.env.VITE_KAMAL_VERSION,
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.5,
    tracePropagationTargets: ["localhost", TRAVILY_ORIGIN],
    enableLogs: true,
    beforeSend: (event, hint) => (isAlreadyHandledNetworkError(event, hint) ? null : event),
  });
}
